import EventEmitter from 'events';
import { Disposable, TextDocumentChangeEvent, TextDocuments } from 'vscode-languageserver';
import { DocumentUri, TextDocument } from 'vscode-languageserver-textdocument';
import { createInterfaceId } from '@gitlab-org/di';
import { TextDocumentChangeListenerType } from './text_document_change_listener_type';
import { HandlesNotification } from './handler';
import { DidChangeDocumentInActiveEditorParams } from './notifications';
import { log } from './log';

function isTextDocument(param: TextDocument | DocumentUri): param is TextDocument {
  return (param as TextDocument).uri !== undefined;
}

export interface TextDocumentChangeListener {
  (event: TextDocumentChangeEvent<TextDocument>, handlerType: TextDocumentChangeListenerType): void;
}

export interface DocumentService {
  onDocumentChange(listener: TextDocumentChangeListener): Disposable;
  notificationHandler(params: DidChangeDocumentInActiveEditorParams): void;
}

export const DocumentService = createInterfaceId<DocumentService>('DocumentsWrapper');

const DOCUMENT_CHANGE_EVENT_NAME = 'documentChange';

export class DefaultDocumentService
  implements DocumentService, HandlesNotification<DidChangeDocumentInActiveEditorParams>
{
  #subscriptions: Disposable[] = [];

  #emitter = new EventEmitter();

  #documents: TextDocuments<TextDocument>;

  constructor(documents: TextDocuments<TextDocument>) {
    this.#documents = documents;

    this.#subscriptions.push(
      documents.onDidOpen(this.#createMediatorListener(TextDocumentChangeListenerType.onDidOpen)),
    );

    documents.onDidChangeContent(
      this.#createMediatorListener(TextDocumentChangeListenerType.onDidChangeContent),
    );

    documents.onDidClose(this.#createMediatorListener(TextDocumentChangeListenerType.onDidClose));

    documents.onDidSave(this.#createMediatorListener(TextDocumentChangeListenerType.onDidSave));
  }

  notificationHandler = (param: DidChangeDocumentInActiveEditorParams) => {
    const document = isTextDocument(param) ? param : this.#documents.get(param);

    if (!document) {
      log.error(`Active editor document cannot be retrieved by URL: ${param}`);
      return;
    }

    const event: TextDocumentChangeEvent<TextDocument> = { document };
    this.#emitter.emit(
      DOCUMENT_CHANGE_EVENT_NAME,
      event,
      TextDocumentChangeListenerType.onDidSetActive,
    );
  };

  #createMediatorListener =
    (type: TextDocumentChangeListenerType) => (event: TextDocumentChangeEvent<TextDocument>) => {
      this.#emitter.emit(DOCUMENT_CHANGE_EVENT_NAME, event, type);
    };

  onDocumentChange(listener: TextDocumentChangeListener) {
    this.#emitter.on(DOCUMENT_CHANGE_EVENT_NAME, listener);
    return {
      dispose: () => this.#emitter.removeListener(DOCUMENT_CHANGE_EVENT_NAME, listener),
    };
  }
}
